import React, { useState } from "react";

// 📆 AGENDA DA SEMANA (18/10/2026, Nelson: "um calendário semanal com as entrevistas agendadas" — e depois: "valeria
// também para o professor e o candidato"). Componente único: a empresa vê as entrevistas; o professor, as aulas; o
// candidato, as duas coisas juntas. Cada página só monta a lista de itens:
//   { chave, quando: Date, titulo, sub, tipo: "entrevista" | "aula", proposto, onClick }
// Confirmado = bloco cheio; proposto/pedido (ainda sem resposta) = tracejado.
export const segundaDe = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };

const COR = {
  entrevista: "bg-green-600 text-white",
  aula: "bg-violet-600 text-white",
};

export default function AgendaSemana({ itens, vazio }) {
  const [inicio, setInicio] = useState(() => segundaDe(new Date()));
  const dias = [...Array(7)].map((_, i) => { const d = new Date(inicio); d.setDate(d.getDate() + i); return d; });
  const fim = new Date(inicio); fim.setDate(fim.getDate() + 7);
  const naSemana = itens.filter((i) => i.quando >= inicio && i.quando < fim).sort((a, b) => a.quando - b.quando);
  const mesmoDia = (a, b) => a.toDateString() === b.toDateString();
  const hoje = new Date();
  const mover = (n) => setInicio((d) => { const x = new Date(d); x.setDate(x.getDate() + 7 * n); return x; });
  const hora = (d) => d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const curto = (d) => d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  const tipos = [...new Set(itens.map((i) => i.tipo))];
  return (
    <section className="mt-6 bg-white rounded-2xl shadow-sm p-4">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => mover(-1)} className="rounded-lg border border-gray-200 px-2.5 py-1 text-sm hover:bg-gray-50" title="Semana anterior">‹</button>
        <button onClick={() => setInicio(segundaDe(new Date()))} className="rounded-lg border border-gray-200 px-3 py-1 text-sm font-semibold hover:bg-gray-50">Hoje</button>
        <button onClick={() => mover(1)} className="rounded-lg border border-gray-200 px-2.5 py-1 text-sm hover:bg-gray-50" title="Próxima semana">›</button>
        <span className="font-semibold text-text ml-1">{curto(dias[0])} – {curto(dias[6])}</span>
        <span className="ml-auto text-xs text-gray-500 flex flex-wrap items-center gap-3">
          {tipos.includes("entrevista") && <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-green-600" /> entrevista</span>}
          {tipos.includes("aula") && <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-violet-600" /> aula</span>}
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded border-2 border-dashed border-amber-400" /> aguardando resposta</span>
        </span>
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-7 gap-2">
        {dias.map((d) => {
          const doDia = naSemana.filter((i) => mesmoDia(i.quando, d));
          const ehHoje = mesmoDia(d, hoje);
          return (
            <div key={d.toISOString()} className={"rounded-xl border p-2 min-h-[7rem] " + (ehHoje ? "border-primary bg-blue-50/40" : "border-gray-200")}>
              <div className={"text-xs font-semibold mb-1 " + (ehHoje ? "text-primary" : "text-gray-500")}>
                {d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "")} {d.getDate()}
              </div>
              {doDia.length === 0 && <div className="text-[11px] text-gray-300">—</div>}
              <div className="space-y-1">
                {doDia.map((i) => (
                  <button key={i.chave} onClick={i.onClick} title={[i.titulo, i.sub].filter(Boolean).join(" · ")}
                    className={"block w-full text-left rounded-lg px-1.5 py-1 text-[11px] leading-tight " +
                      (i.proposto ? "border-2 border-dashed border-amber-300 bg-amber-50 text-amber-900" : COR[i.tipo] || COR.entrevista)}>
                    <b>{hora(i.quando)}</b> {i.tipo === "aula" ? "🎓" : "📅"} {i.titulo}
                    {i.sub && <div className="opacity-80 truncate">{i.sub}</div>}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {naSemana.length === 0 && vazio && <p className="text-xs text-gray-500 mt-3">{vazio}</p>}
    </section>
  );
}

// Adaptadores: entrevista e aula → itens da agenda.
export function itensDeEntrevistas(entrevistas, nomeOutro, onClick) {
  const itens = [];
  (entrevistas || []).forEach((e) => {
    if (e.inicio && ["confirmada", "realizada"].includes(e.status))
      itens.push({ chave: `e${e.id}`, quando: new Date(e.inicio), titulo: nomeOutro(e), sub: e.vaga, tipo: "entrevista", proposto: false, onClick: () => onClick(e) });
    if (e.status === "proposta")
      (e.horarios || []).forEach((h) => itens.push({ chave: `e${e.id}-${h}`, quando: new Date(h), titulo: nomeOutro(e), sub: e.vaga, tipo: "entrevista", proposto: true, onClick: () => onClick(e) }));
  });
  return itens;
}

export function itensDeAulas(aulas, onClick) {
  const itens = [];
  (aulas || []).forEach((a) => {
    const titulo = a.com || "Aula";
    const sub = a.skill ? `Aula de ${a.skill}` : "Aula grátis";
    if (["confirmada", "realizada"].includes(a.status))
      itens.push({ chave: `a${a.id}`, quando: new Date(a.inicio), titulo, sub, tipo: "aula", proposto: false, onClick: () => onClick(a) });
    if (a.status === "pedida")
      itens.push({ chave: `a${a.id}`, quando: new Date(a.inicio), titulo, sub, tipo: "aula", proposto: true, onClick: () => onClick(a) });
    if (a.status === "sugerida" && a.sugeridoInicio)
      itens.push({ chave: `a${a.id}-s`, quando: new Date(a.sugeridoInicio), titulo, sub, tipo: "aula", proposto: true, onClick: () => onClick(a) });
  });
  return itens;
}
